import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, CalendarDays, ArrowRight, Loader2, Tag, ChevronLeft, ChevronRight } from "lucide-react";
import axios from "axios";
import { BRAND } from "../data/mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso.slice(0, 10);
  }
}

function resolveImg(url) {
  if (!url) return null;
  if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
}

export default function Blog() {
  const [params, setParams] = useSearchParams();
  const page = Number(params.get("page") || 1);
  const category = params.get("category") || "all";
  const q = params.get("q") || "";
  const [search, setSearch] = useState(q);
  const [data, setData] = useState({ items: [], total: 0, pages: 0 });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Blog — BarknFurs Grooming Studio";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Pet grooming tips, dog care guides and pet parenting advice from BarknFurs, Bhubaneswar's trusted grooming studio.");
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const qs = new URLSearchParams({ page, per_page: 9 });
        if (category !== "all") qs.set("category", category);
        if (q) qs.set("q", q);
        const [list, cats] = await Promise.all([
          axios.get(`${API}/blog?${qs.toString()}`),
          axios.get(`${API}/blog/categories`),
        ]);
        if (!cancelled) {
          setData(list.data);
          setCategories(cats.data.rows || []);
        }
      } catch { /* ignore */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [page, category, q]);

  const handleSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (search.trim()) next.set("q", search.trim()); else next.delete("q");
    next.set("page", "1");
    setParams(next);
  };

  const setCat = (c) => {
    const next = new URLSearchParams(params);
    if (c === "all") next.delete("category"); else next.set("category", c);
    next.set("page", "1");
    setParams(next);
  };

  const goPage = (p) => {
    const next = new URLSearchParams(params);
    next.set("page", String(p));
    setParams(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12" data-testid="public-blog">
      <header className="mb-10">
        <p className="text-[#C9BCFF] uppercase tracking-[0.25em] text-xs mb-3">Journal</p>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white">
          Grooming tips, pet care &amp; stories
        </h1>
        <p className="text-white/60 mt-4 max-w-2xl">
          Practical advice for Bhubaneswar pet parents — from our groomers, vets we work with, and the 500+ furry friends who visit us every month.
        </p>
      </header>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <form onSubmit={handleSearch} className="relative w-full md:max-w-md" data-testid="blog-search-form">
          <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="blog-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search grooming tips, breeds, services…"
            className="w-full bg-[#2E0F94] border border-white/10 rounded-full pl-10 pr-4 py-2.5 text-sm text-white placeholder-white/40 focus:border-[#FC5106] focus:ring-1 focus:ring-[#FC5106] outline-none"
          />
        </form>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setCat("all")}
            data-testid="blog-category-all"
            className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-full border ${
              category === "all" ? "bg-[#FC5106] border-[#FC5106] text-white" : "bg-[#2E0F94] border-white/10 text-white/70 hover:border-white/20"
            }`}
          >
            All
          </button>
          {categories.map((c) => (
            <button
              key={c.name}
              onClick={() => setCat(c.name)}
              data-testid={`blog-category-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-full border ${
                category === c.name ? "bg-[#FC5106] border-[#FC5106] text-white" : "bg-[#2E0F94] border-white/10 text-white/70 hover:border-white/20"
              }`}
            >
              {c.name} <span className="text-white/40 ml-1">{c.count}</span>
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-7 h-7 text-[#FC5106] animate-spin" /></div>
      ) : data.items.length === 0 ? (
        <div className="text-center py-20 border border-white/10 rounded-2xl bg-[#2E0F94]">
          <p className="text-white/60">No posts yet. Check back soon — we're writing.</p>
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
            {data.items.map((post) => (
              <BlogCard key={post.id} post={post} />
            ))}
          </div>

          {data.pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-12" data-testid="blog-pagination">
              <button
                onClick={() => goPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="p-2 rounded-full bg-[#2E0F94] border border-white/10 text-white disabled:opacity-40 disabled:cursor-not-allowed hover:border-white/20"
                aria-label="Previous page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {Array.from({ length: data.pages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => goPage(p)}
                  data-testid={`blog-page-${p}`}
                  className={`w-9 h-9 rounded-full text-sm font-semibold border ${
                    p === page ? "bg-[#FC5106] border-[#FC5106] text-white" : "bg-[#2E0F94] border-white/10 text-white/70 hover:border-white/20"
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => goPage(Math.min(data.pages, page + 1))}
                disabled={page === data.pages}
                className="p-2 rounded-full bg-[#2E0F94] border border-white/10 text-white disabled:opacity-40 disabled:cursor-not-allowed hover:border-white/20"
                aria-label="Next page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}

      <section className="mt-20 rounded-3xl bg-gradient-to-br from-[#FC5106]/20 via-transparent to-white/10 border border-white/10 p-8 sm:p-12 text-center">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white">Ready to pamper your pet?</h2>
        <p className="text-white/60 mt-2 max-w-xl mx-auto">
          Book a grooming session at our Bhubaneswar studio or at your doorstep.
        </p>
        <div className="flex flex-wrap justify-center gap-3 mt-6">
          <Link to="/contact" data-testid="blog-cta-book" className="btn-primary !py-3 !px-6 !text-sm">
            Book grooming now
          </Link>
          <a href={`https://wa.me/${BRAND.whatsapp}`} target="_blank" rel="noreferrer"
            data-testid="blog-cta-whatsapp"
            className="inline-flex items-center gap-2 bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 rounded-full px-6 py-3 text-sm font-semibold">
            WhatsApp us
          </a>
        </div>
      </section>
    </div>
  );
}

function BlogCard({ post }) {
  return (
    <article className="group rounded-2xl overflow-hidden bg-[#2E0F94] border border-white/10 hover:border-[#FC5106]/40 transition-colors" data-testid={`blog-card-${post.slug}`}>
      <Link to={`/blog/${post.slug}`} className="block">
        <div className="aspect-[16/10] bg-[#1F0A66]/40 overflow-hidden">
          {post.featured_image_url ? (
            <img
              src={resolveImg(post.featured_image_url)}
              alt={post.featured_image_alt || post.title}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#FC5106]/30 to-white/20 flex items-center justify-center">
              <Tag className="w-10 h-10 text-white/30" />
            </div>
          )}
        </div>
        <div className="p-5 space-y-3">
          <div className="flex items-center gap-2 text-xs text-white/50">
            <span className="inline-flex items-center gap-1"><CalendarDays className="w-3 h-3" />{formatDate(post.published_at || post.created_at)}</span>
            <span>·</span>
            <span className="uppercase tracking-wider text-[#C9BCFF]">{post.category || "General"}</span>
          </div>
          <h2 className="font-heading font-bold text-lg text-white group-hover:text-[#FC5106] transition-colors line-clamp-2">
            {post.title}
          </h2>
          <p className="text-white/60 text-sm line-clamp-3">{post.excerpt}</p>
          <span className="inline-flex items-center gap-1 text-sm text-[#FC5106] font-semibold">
            Read more <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </span>
        </div>
      </Link>
    </article>
  );
}
